import { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { MatchState, SniperMarket } from '../../types/sniper.types';
import {
  isArmed,
  normalizeDecimalInput,
  targetSummary,
  venueColor,
  venueLabel,
} from '../../utils/sniperArm';

/**
 * One Sniper market row: venue chip · label · counter/team/line · the three
 * per-row editable params (size / max price / confidence gate) with a DRAFT +
 * ✓ confirm (mirror of the opener entry table — edits stay local until the
 * operator commits, so a typo isn't written mid-keystroke) · an enabled toggle
 * (commits immediately) · a live ARMED badge computed from the feed-derived
 * `MatchState` (`count == floor(line)`).
 */
export const SniperMarketRow = memo(function SniperMarketRow({
  market,
  state,
  onPatch,
  onDelete,
  busy,
}: {
  market: SniperMarket;
  /** Feed-derived live state; null until the event is active + streaming. */
  state: MatchState | null;
  onPatch: (patch: {
    size?: number;
    maxPrice?: number;
    confidenceGate?: number;
    enabled?: boolean;
  }) => void;
  onDelete: () => void;
  busy?: boolean;
}) {
  // Draft strings — free text while editing, committed on ✓.
  const [size, setSize] = useState(String(market.size));
  const [maxPrice, setMaxPrice] = useState(String(market.maxPrice));
  const [gate, setGate] = useState(String(market.confidenceGate));

  // Re-seed drafts when the persisted row changes (e.g. after a save elsewhere).
  useEffect(() => {
    setSize(String(market.size));
    setMaxPrice(String(market.maxPrice));
    setGate(String(market.confidenceGate));
  }, [market.size, market.maxPrice, market.confidenceGate]);

  const parsed = useMemo(() => {
    const s = Number(size);
    const m = Number(maxPrice);
    const g = Number(gate);
    return {
      size: s,
      maxPrice: m,
      gate: g,
      valid:
        Number.isFinite(s) &&
        s > 0 &&
        Number.isFinite(m) &&
        m > 0 &&
        m <= 1 &&
        Number.isFinite(g) &&
        g >= 0 &&
        g <= 1,
    };
  }, [size, maxPrice, gate]);

  const dirty =
    parsed.size !== market.size ||
    parsed.maxPrice !== market.maxPrice ||
    parsed.gate !== market.confidenceGate;

  const armed = state
    ? isArmed(market.counter, state, market.line, market.team)
    : false;

  const commit = () => {
    if (!dirty || !parsed.valid) return;
    onPatch({
      size: parsed.size,
      maxPrice: parsed.maxPrice,
      confidenceGate: parsed.gate,
    });
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        border: 1,
        borderColor: armed ? '#ff5252' : 'divider',
        bgcolor: armed ? 'rgba(255,82,82,0.08)' : 'transparent',
        flexWrap: 'wrap',
      }}
    >
      <Chip
        size="small"
        label={venueLabel(market.venue)}
        sx={{
          bgcolor: venueColor(market.venue),
          color: '#fff',
          fontWeight: 700,
          height: 22,
        }}
      />
      <Box sx={{ minWidth: 180, flex: 1 }}>
        <Typography variant="body2" sx={{ color: 'common.white' }}>
          {market.marketLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {targetSummary(market.counter, market.line, market.team)}
        </Typography>
      </Box>

      {/* ARMED badge — live, from the feed-derived state. */}
      {armed && (
        <Chip
          size="small"
          label="ARMED"
          sx={{
            bgcolor: '#ff5252',
            color: '#fff',
            fontWeight: 800,
            letterSpacing: 0.5,
            height: 22,
          }}
        />
      )}

      <TextField
        size="small"
        label="Size"
        value={size}
        onChange={(e) => setSize(normalizeDecimalInput(e.target.value))}
        inputProps={{ inputMode: 'decimal', 'aria-label': 'sweep size' }}
        sx={{ width: 84 }}
      />
      <TextField
        size="small"
        label="Max $"
        value={maxPrice}
        onChange={(e) => setMaxPrice(normalizeDecimalInput(e.target.value))}
        inputProps={{ inputMode: 'decimal', 'aria-label': 'max price' }}
        sx={{ width: 84 }}
      />
      <TextField
        size="small"
        label="Gate"
        value={gate}
        onChange={(e) => setGate(normalizeDecimalInput(e.target.value))}
        inputProps={{ inputMode: 'decimal', 'aria-label': 'confidence gate' }}
        sx={{ width: 84 }}
      />

      <Tooltip title={parsed.valid ? 'Save changes' : 'Fix the values first'}>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={commit}
            disabled={!dirty || !parsed.valid || busy}
            aria-label="confirm market params"
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={market.enabled ? 'Enabled' : 'Disabled (fail-safe OFF)'}>
        <Switch
          size="small"
          checked={market.enabled}
          onChange={(e) => onPatch({ enabled: e.target.checked })}
          disabled={busy}
          inputProps={{ 'aria-label': 'enable market' }}
        />
      </Tooltip>

      <Tooltip title="Delete market">
        <IconButton
          size="small"
          onClick={onDelete}
          disabled={busy}
          aria-label="delete market"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
});
